// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import * as api from '../../api/client'
import MentionTextarea from './MentionTextarea'

export interface ThreadComment {
  id: string
  author_display_name: string
  author_role: string
  author_account_id?: string | null
  body: string
  created_at: string
  edited_at?: string | null
  mentioned_account_ids?: string | null
}

function parseMentions(json?: string | null): string[] {
  if (!json) return []
  try { return JSON.parse(json) as string[] } catch { return [] }
}

export default function CommentThread({ items, currentAccountId, headerNote, onPost, onEdit, onDelete, reload }: {
  items: ThreadComment[]
  currentAccountId?: string | null
  headerNote?: string
  onPost: (body: string, mentions: string[]) => Promise<void>
  onEdit: (id: string, body: string, mentions: string[]) => Promise<void>
  onDelete: (id: string) => Promise<void>
  reload: () => void
}) {
  const [body, setBody] = useState('')
  const [mentions, setMentions] = useState<api.MentionUser[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [editId, setEditId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const [editMentions, setEditMentions] = useState<api.MentionUser[]>([])
  const [confirmDel, setConfirmDel] = useState<string | null>(null)

  // Only submit mentions whose "@Name" is still present in the text.
  const usedIds = (text: string, ms: api.MentionUser[]) => ms.filter(m => text.includes('@' + m.display_name)).map(m => m.id)

  async function submit() {
    const text = body.trim()
    if (!text) return
    setBusy(true); setErr(null)
    try { await onPost(text, usedIds(text, mentions)); setBody(''); setMentions([]); reload() }
    catch (e: any) { setErr(e?.message || 'Could not post comment.') }
    finally { setBusy(false) }
  }
  async function saveEdit(c: ThreadComment) {
    const text = editBody.trim()
    if (!text) return
    setBusy(true); setErr(null)
    // Preserve existing tags, add any newly typed ones.
    const merged = Array.from(new Set([...parseMentions(c.mentioned_account_ids), ...usedIds(text, editMentions)]))
    try { await onEdit(c.id, text, merged); setEditId(null); setEditMentions([]); reload() }
    catch (e: any) { setErr(e?.message || 'Could not save edit.') }
    finally { setBusy(false) }
  }
  async function remove(id: string) {
    setBusy(true); setErr(null)
    try { await onDelete(id); setConfirmDel(null); reload() }
    catch (e: any) { setErr(e?.message || 'Could not delete comment.') }
    finally { setBusy(false) }
  }

  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--color-primary,#2563eb)', cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'inherit' }

  return (
    <div style={{ marginTop: 18 }}>
      <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>Comments</h2>
      {headerNote && <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 8 }}>{headerNote}</div>}

      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--color-text-mute)', marginBottom: 8 }}>No comments yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {items.map(c => {
            const mine = !!currentAccountId && c.author_account_id === currentAccountId
            return (
              <div key={c.id} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '8px 10px', background: 'var(--color-surface)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
                    {c.author_display_name} · {c.author_role.replace('_', ' ')} · {new Date(c.created_at).toLocaleString()}{c.edited_at ? ' · edited' : ''}
                  </div>
                  {mine && editId !== c.id && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button style={linkBtn} onClick={() => { setEditId(c.id); setEditBody(c.body); setEditMentions([]) }}>Edit</button>
                      {confirmDel === c.id ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                          <span style={{ color: '#B23A22' }}>Delete?</span>
                          <button style={{ ...linkBtn, color: '#B23A22' }} onClick={() => remove(c.id)} disabled={busy}>Yes</button>
                          <button style={{ ...linkBtn, color: 'var(--color-text-mute)' }} onClick={() => setConfirmDel(null)}>No</button>
                        </span>
                      ) : (
                        <button style={{ ...linkBtn, color: 'var(--color-text-mute)' }} onClick={() => setConfirmDel(c.id)}>Delete</button>
                      )}
                    </div>
                  )}
                </div>
                {editId === c.id ? (
                  <div style={{ marginTop: 6 }}>
                    <MentionTextarea value={editBody} onChange={setEditBody} mentions={editMentions} setMentions={setEditMentions} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button style={linkBtn} onClick={() => saveEdit(c)} disabled={busy || !editBody.trim()}>Save</button>
                      <button style={{ ...linkBtn, color: 'var(--color-text-mute)' }} onClick={() => setEditId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', marginTop: 2 }}>{c.body}</div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {err && <div style={{ color: 'var(--color-danger,#b91c1c)', fontSize: 12, marginBottom: 6 }}>{err}</div>}
      <MentionTextarea value={body} onChange={setBody} mentions={mentions} setMentions={setMentions} placeholder="Add a comment…  type @ to tag a teammate" />
      {mentions.filter(m => body.includes('@' + m.display_name)).length > 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 4 }}>
          Tagging: {mentions.filter(m => body.includes('@' + m.display_name)).map(m => '@' + m.display_name).join(', ')}
        </div>
      )}
      <div style={{ marginTop: 6 }}>
        <button onClick={submit} disabled={busy || !body.trim()} style={{
          background: 'var(--color-primary,#2563eb)', color: '#fff', border: 'none', borderRadius: 8,
          padding: '7px 16px', fontSize: 13, cursor: busy || !body.trim() ? 'default' : 'pointer', fontFamily: 'inherit',
          opacity: busy || !body.trim() ? 0.6 : 1 }}>
          {busy ? 'Posting…' : 'Post comment'}
        </button>
      </div>
    </div>
  )
}
